interface OwnerBusinessInfo {
    id: string
    name: string
}

export interface WhatsappBusinessAccount {
    id: string
    name: string
    timezone_id: string
    message_template_namespace: string
    created_at: string
    updated_at: string
    client_id: string
    primary_funding_id?: string
    owner_business_info: OwnerBusinessInfo
}

export interface Webhook {
    id: string
    enabled: boolean
    url: string
    headers?: Record<string, string>
    created_at?: Date
    updated_at?: Date
    status?: string
    last_error?: string
    last_error_at?: Date
    disabled_at?: Date
    error_count?: number
    error_limit?: number
}

export interface Throughput {
    level: string
}

export interface PhoneNumber {
    waba_id?: string
    verified_name: string
    code_verification_status: string
    display_phone_number: string
    quality_rating: string
    platform_type: string
    throughput: Throughput
    id: string
    webhooks: Record<string, Webhook>
    testing_mode: boolean
}

export interface createPhoneNumberPayload {
    cc: string
    phone_number: string
    verified_name: string
}

export interface Cursors {
    before: string
    after: string
}

export interface Paging {
    cursors: Cursors
}

export interface PagedPhoneNumbers {
    data: PhoneNumber[]
    paging: Paging
}

export interface Parameter {
    id: string
    type: string
    value: string
}

export interface TextParameter {
    type: string
    text: string
}

export interface QuickReplyButton {
    type: string
    text: string
}

export interface PhoneNumberButton {
    type: string
    phone_number: string
    text: string
}

export interface UrlButton {
    type: string
    url: string
    text: string
}

export interface CopyCodeButton {
    type: string
    example: string[]
    text: string
}

interface MultimediaHeaderExample {
    header_handle: string[]
}

export interface HeaderComponent {
    type: string
    text: string
    format: string
    parameters: TextParameter[]
    example?: MultimediaHeaderExample | null
}

export interface BodyComponent {
    type: string
    text: string
    parameters: TextParameter[]
}

export interface FooterComponent {
    type: string
    text: string
}

export interface ButtonsComponent {
    type: string
    buttons: (QuickReplyButton | PhoneNumberButton | UrlButton | CopyCodeButton)[]
}

export type MessageComponent = (HeaderComponent | BodyComponent | FooterComponent | ButtonsComponent)

export interface MessageTemplate {
    name: string
    namespace: string
    element_name: string
    language: string
    category: string
    components: MessageComponent[]
    status: string
    created_at: Date
    updated_at: Date
    id: string
    account_id: string
    phone_number_id: string
    rejected_reason: string
}

interface WhatsappTemplateLanguage {
    code: string
}

interface WhatsappBaseParameter {
    type: string
    text?: string
}

interface WhatsappTextParameter extends WhatsappBaseParameter {
    type: 'text'
    index: string
}

interface WhatsappCurrency {
    fallback_value: string
    code: string
    amount_1000: number
}

interface WhatsappCurrencyParameter extends WhatsappBaseParameter {
    type: 'currency'
    currency: WhatsappCurrency
}

interface WhatsappDateTime {
    fallback_value: string
}

interface WhatsappDateTimeParameter extends WhatsappBaseParameter {
    type: 'date_time'
    date_time: WhatsappDateTime
}

interface WhatsappQuickReplyButton extends WhatsappBaseParameter {
    type: 'payload'
    payload: string
}

interface WhatsappPhoneNumberButton extends WhatsappBaseParameter {
    type: 'phone_number'
    text: string
}

interface WhatsappUrlButton extends WhatsappBaseParameter {
    type: 'url'
    text: string
}

interface WhatsappCopyCodeButton extends WhatsappBaseParameter {
    type: 'coupon_code'
    coupon_code: string
}

export interface TextHeaderParameter {
    type: 'text'
    text: string
    id?: number
}

export interface ImageHeaderParameter {
    type: 'image'
    image: {
        link: string
        default?: string
    }
}

export interface VideoHeaderParameter {
    type: 'video'
    video: {
        link: string
        default?: string
    }
}

export interface DocumentHeaderParameter {
    type: 'document'
    document: {
        link: string
        filename: string
        default?: string
    }
}

export interface LocationHeaderParameter {
    type: 'location'
    location: {
        latitude: string
        longitude: string
        name: string
        address: string
    }
}

export type WhatsappTemplateHeaderParameter = TextHeaderParameter | ImageHeaderParameter | VideoHeaderParameter | DocumentHeaderParameter | LocationHeaderParameter

export function isTextHeaderParameter (param: any): param is TextHeaderParameter {
  return param.type.toLowerCase() === 'text'
}

export function isImageHeaderParameter (param: any): param is ImageHeaderParameter {
  return param.type.toLowerCase() === 'image'
}

export function isVideoHeaderParameter (param: any): param is VideoHeaderParameter {
  return param.type.toLowerCase() === 'video'
}

export function isDocumentHeaderParameter (param: any): param is DocumentHeaderParameter {
  return param.type.toLowerCase() === 'document'
}

export function isLocationHeaderParameter (param: any): param is LocationHeaderParameter {
  return param.type.toLowerCase() === 'location'
}

type WhatsappTemplateParameter = WhatsappTextParameter | WhatsappCurrencyParameter | WhatsappDateTimeParameter | WhatsappQuickReplyButton | WhatsappUrlButton | WhatsappPhoneNumberButton | WhatsappCopyCodeButton

interface WhatsappBaseComponent {
    type: string
    parameters?: WhatsappTemplateParameter[] | WhatsappTemplateHeaderParameter[]
}

interface WhatsappButtonComponent extends WhatsappBaseComponent {
    type: 'button'
    sub_type: string
    index: string
    text?: string
}

interface WhatsappBodyComponent extends WhatsappBaseComponent {
    type: 'body'
    text?: string
}

export interface WhatsappHeaderComponent extends WhatsappBaseComponent {
    type: 'header'
    text?: string
    format?: string
}

interface WhatsappFooterComponent extends WhatsappBaseComponent {
    type: 'footer'
    text?: string
}

type WhatsappTemplateComponent = WhatsappHeaderComponent | WhatsappBodyComponent | WhatsappFooterComponent | WhatsappButtonComponent

export function isWhatsappHeaderComponent (component: WhatsappTemplateComponent): component is WhatsappHeaderComponent {
  return component.type.toLowerCase() === 'header'
}

export interface WhatsappTemplate {
    name: string
    language: WhatsappTemplateLanguage
    components: WhatsappTemplateComponent[]
}

interface HeaderTextExample {
    header_text: string[]
}

export interface CreateableHeaderComponent {
    type: string
    format: string
    text?: string | null
    example?: HeaderTextExample | MultimediaHeaderExample | null
}

export interface CreateableLocationHeaderComponent {
    type: string
    format: string
}

interface BodyTextExample {
    body_text: string[][]
}

export interface CreateableBodyComponent {
    type: string
    text: string
    example?: BodyTextExample | null
}

export interface CreateableFooterComponent {
    type: string
    text: string
}

export interface ButtonTypePhoneNumber {
    type: string
    text: string
    phone_number: string
}

export interface ButtonTypeURL {
    type: string
    text: string
    url: string
    example: string[] | null
}

export interface ButtonTypeQuickReply {
    type: string
    text: string
}

export interface ButtonTypeCouponCode {
    type: string
    text: string
    coupon_code?: string
    example: string[]
}

export type ButtonType = ButtonTypePhoneNumber | ButtonTypeURL | ButtonTypeQuickReply | ButtonTypeCouponCode

export interface CreateableButtonsComponent {
    type: string
    buttons: ButtonType[]
}

export type CreateableTemplateComponent = CreateableHeaderComponent | CreateableLocationHeaderComponent | CreateableBodyComponent | CreateableFooterComponent | CreateableButtonsComponent

export interface WhatsappBusinessProfile {
    about: string;
    address: string;
    description: string;
    email: string;
    messaging_product: string;
    profile_picture_url: string;
    websites: string[];
    vertical: string;
    profile_picture_handle?: string
}

export interface WhatsAppBusinessProfileResponse {
    data: WhatsappBusinessProfile[]
}
